/**
 * Mock Data for Revit API
 *
 * This file contains centralized mock data used for development and testing.
 * When the real Revit WebView2 integration is implemented, this data will be
 * replaced by actual API calls to the Revit add-in.
 */

import {
  RevitParameter,
  RevitElement,
  RevitSettings,
  RevitFamily,
  RevitParameterOption,
  RevitConnectorInfo,
  RevitEndType,
  RevitParameterGroup,
} from './types';

// =============================================================================
// Mock Revit Parameters
// =============================================================================

/**
 * Complete list of Revit parameters used in the PipeEX application.
 * These parameters are typically shared parameters added to piping families.
 */
export const MOCK_REVIT_PARAMETERS: RevitParameter[] = [
  // -------------------------------------------------------------------------
  // Connection/Endpoint Parameters
  // -------------------------------------------------------------------------
  {
    id: 'param-001',
    name: 'PiX_PPG_END1_TYPE',
    category: 'Connections',
    dataType: 'string',
    description: 'End 1 connection type (Primary connector)',
    isInstance: true,
    group: 'Endpoints',
    tooltip: 'Connection type for the primary endpoint (port1)',
  },
  {
    id: 'param-002',
    name: 'PiX_PPG_END2_TYPE',
    category: 'Connections',
    dataType: 'string',
    description: 'End 2 connection type (Secondary connector)',
    isInstance: true,
    group: 'Endpoints',
    tooltip: 'Connection type for the secondary endpoint (port2)',
  },
  {
    id: 'param-003',
    name: 'PiX_PPG_END3_TYPE',
    category: 'Connections',
    dataType: 'string',
    description: 'End 3 connection type (Perpendicular to port1/port2)',
    isInstance: true,
    group: 'Endpoints',
    tooltip: 'Connection type for the third endpoint (port3)',
  },
  {
    id: 'param-004',
    name: 'PiX_PPG_END4_TYPE',
    category: 'Connections',
    dataType: 'string',
    description: 'End 4 connection type',
    isInstance: true,
    group: 'Endpoints',
    tooltip: 'Connection type for the fourth endpoint (port4)',
  },
  {
    id: 'param-005',
    name: 'PiX_PPG_END1_PressureRating',
    category: 'Pressure',
    dataType: 'string',
    description: 'Pressure rating for End 1',
    isInstance: true,
    group: 'Pressure',
    tooltip: 'Pressure rating specification for the primary endpoint',
  },
  {
    id: 'param-006',
    name: 'PiX_PPG_END2_PressureRating',
    category: 'Pressure',
    dataType: 'string',
    description: 'Pressure rating for End 2',
    isInstance: true,
    group: 'Pressure',
    tooltip: 'Pressure rating specification for the secondary endpoint',
  },
  {
    id: 'param-007',
    name: 'PiX_PPG_END3_PressureRating',
    category: 'Pressure',
    dataType: 'string',
    description: 'Pressure rating for End 3',
    isInstance: true,
    group: 'Pressure',
    tooltip: 'Pressure rating specification for the third endpoint',
  },
  {
    id: 'param-008',
    name: 'PiX_PPG_END4_PressureRating',
    category: 'Pressure',
    dataType: 'string',
    description: 'Pressure rating for End 4',
    isInstance: true,
    group: 'Pressure',
    tooltip: 'Pressure rating specification for the fourth endpoint',
  },

  // -------------------------------------------------------------------------
  // Specification Parameters
  // -------------------------------------------------------------------------
  {
    id: 'param-009',
    name: 'PiP_PPG_SPEC',
    category: 'Specifications',
    dataType: 'string',
    description: 'Piping specification',
    isInstance: true,
    group: 'Piping',
    tooltip: 'Piping specification code (e.g., 10CS21, 16SS21)',
  },
  {
    id: 'param-010',
    name: 'PiX_02_Standard_ENG',
    category: 'Standard',
    dataType: 'string',
    description: 'Design standard (English)',
    isInstance: false,
    group: 'Standard',
    tooltip: 'Design standard reference (e.g., ASME B31.3, EN 13480)',
  },
  {
    id: 'param-011',
    name: 'PiX_03_Material_ENG',
    category: 'Material',
    dataType: 'string',
    description: 'Material specification (English)',
    isInstance: false,
    group: 'Material',
    tooltip: 'Material field used in the bill of materials.',
  },

  // -------------------------------------------------------------------------
  // Description Parameters
  // -------------------------------------------------------------------------
  {
    id: 'param-012',
    name: 'PiX_01_Description_ENG',
    category: 'Description',
    dataType: 'string',
    description: 'Item description (English)',
    isInstance: false,
    group: 'General',
    tooltip: 'Human-readable description of the component',
  },
  {
    id: 'param-013',
    name: 'PiX_01_Description_LOC',
    category: 'Description',
    dataType: 'string',
    description: 'Item description (Local language)',
    isInstance: false,
    group: 'General',
    tooltip: 'Localized description of the component',
  },

  // -------------------------------------------------------------------------
  // Insulation Parameters
  // -------------------------------------------------------------------------
  {
    id: 'param-014',
    name: 'PiX_GN_2_InsulationSpec',
    category: 'Insulation',
    dataType: 'string',
    description: 'Insulation type specification',
    isInstance: true,
    group: 'Insulation',
    tooltip: 'Type of insulation applied (PP, HC, etc.)',
  },
  {
    id: 'param-015',
    name: 'PiX_GN_2_InsulationThickness',
    category: 'Insulation',
    dataType: 'string',
    description: 'Insulation thickness',
    isInstance: true,
    group: 'Insulation',
    tooltip: 'Thickness of insulation in mm',
  },
  {
    id: 'param-016',
    name: 'Insulation Type',
    category: 'Insulation',
    dataType: 'string',
    description: 'Revit built-in insulation type parameter',
    isInstance: true,
    group: 'Insulation',
    tooltip: 'Built-in Revit parameter for insulation type',
  },
  {
    id: 'param-017',
    name: 'Insulation Thickness',
    category: 'Insulation',
    dataType: 'length',
    description: 'Revit built-in insulation thickness parameter',
    isInstance: true,
    group: 'Insulation',
    tooltip: 'Built-in Revit parameter for insulation thickness',
  },

  // -------------------------------------------------------------------------
  // Heat Tracing Parameters
  // -------------------------------------------------------------------------
  {
    id: 'param-018',
    name: 'PiX_GN_2_HeatTracingSpec',
    category: 'Tracing',
    dataType: 'string',
    description: 'Heat tracing specification',
    isInstance: true,
    group: 'Tracing',
    tooltip: 'Heat tracing specification (Steam, Electric, etc.)',
  },

  // -------------------------------------------------------------------------
  // Weight Parameters
  // -------------------------------------------------------------------------
  {
    id: 'param-019',
    name: 'PiX_06_Mass_kg_Piping',
    category: 'General',
    dataType: 'number',
    description: 'Mass in kilograms for piping',
    isInstance: false,
    group: 'Weight',
    tooltip: 'Component weight in kilograms',
  },
  {
    id: 'param-020',
    name: 'PiX_06_Mass_kg_Support',
    category: 'General',
    dataType: 'number',
    description: 'Mass in kilograms for support',
    isInstance: false,
    group: 'Weight',
    tooltip: 'Support weight in kilograms',
  },

  // -------------------------------------------------------------------------
  // Identity Parameters
  // -------------------------------------------------------------------------
  {
    id: 'param-021',
    name: 'PiX_GN_4_ItemTag',
    category: 'Identity',
    dataType: 'string',
    description: 'Item tag identifier',
    isInstance: true,
    group: 'General',
    tooltip: 'Unique tag for component identification',
  },
  {
    id: 'param-022',
    name: 'PiX_GN_5_SSReference',
    category: 'Identity',
    dataType: 'string',
    description: 'Support structure reference',
    isInstance: true,
    group: 'General',
    tooltip: 'Reference to associated support structure',
  },

  // -------------------------------------------------------------------------
  // PCF Export Parameters
  // -------------------------------------------------------------------------
  {
    id: 'param-023',
    name: 'SKEY',
    category: 'General',
    dataType: 'string',
    description: 'Symbol key for PCF export',
    isInstance: false,
    group: 'General',
    tooltip: 'PCF symbol key (EL90, FLWN, etc.)',
  },
  {
    id: 'param-024',
    name: 'OBJECT-TYPE',
    category: 'General',
    dataType: 'string',
    description: 'Object type for PCF export',
    isInstance: false,
    group: 'General',
    tooltip: 'PCF object type classification',
  },
  {
    id: 'param-025',
    name: 'END-POINT_1',
    category: 'Endpoints',
    dataType: 'string',
    description: 'End point 1 coordinates',
    isInstance: true,
    group: 'Endpoints',
    tooltip: 'XYZ coordinates for end point 1',
  },
  {
    id: 'param-026',
    name: 'END-POINT_2',
    category: 'Endpoints',
    dataType: 'string',
    description: 'End point 2 coordinates',
    isInstance: true,
    group: 'Endpoints',
    tooltip: 'XYZ coordinates for end point 2',
  },
  {
    id: 'param-027',
    name: 'END-POINT_3',
    category: 'Endpoints',
    dataType: 'string',
    description: 'End point 3 coordinates',
    isInstance: true,
    group: 'Endpoints',
    tooltip: 'XYZ coordinates for end point 3',
  },
  {
    id: 'param-028',
    name: 'END-POINT_4',
    category: 'Endpoints',
    dataType: 'string',
    description: 'End point 4 coordinates',
    isInstance: true,
    group: 'Endpoints',
    tooltip: 'XYZ coordinates for end point 4',
  },
  {
    id: 'param-029',
    name: 'ITEM-DESCRIPTION',
    category: 'Description',
    dataType: 'string',
    description: 'Item description for PCF',
    isInstance: false,
    group: 'General',
    tooltip: 'PCF item description field',
  },
  {
    id: 'param-030',
    name: 'PressureClass',
    category: 'Pressure',
    dataType: 'string',
    description: 'Pressure class rating',
    isInstance: false,
    group: 'Pressure',
    tooltip: 'Pressure class (150, 300, 600, etc.)',
  },
  {
    id: 'param-031',
    name: 'Material',
    category: 'Material',
    dataType: 'string',
    description: 'Material type',
    isInstance: false,
    group: 'Material',
    tooltip: 'Material specification',
  },
  {
    id: 'param-032',
    name: 'DesignStd',
    category: 'Standard',
    dataType: 'string',
    description: 'Design standard code',
    isInstance: false,
    group: 'Standard',
    tooltip: 'Design standard reference code',
  },
  {
    id: 'param-033',
    name: 'INSULATIONTHICKNESS',
    category: 'Insulation',
    dataType: 'number',
    description: 'PCF insulation thickness',
    isInstance: true,
    group: 'Insulation',
    tooltip: 'Insulation thickness for PCF export',
  },
  {
    id: 'param-034',
    name: 'Description',
    category: 'Description',
    dataType: 'string',
    description: 'Generic description parameter',
    isInstance: false,
    group: 'General',
    tooltip: 'General description field',
  },

  // -------------------------------------------------------------------------
  // Process Condition Parameters
  // -------------------------------------------------------------------------
  {
    id: 'param-035',
    name: 'PiX_PC_1_DesignTemperature',
    category: 'Process',
    dataType: 'number',
    description: 'Design temperature',
    isInstance: true,
    group: 'Process',
    tooltip: 'Design temperature in degrees',
  },
  {
    id: 'param-036',
    name: 'PiX_PC_2_OperatingTemperature',
    category: 'Process',
    dataType: 'number',
    description: 'Operating temperature',
    isInstance: true,
    group: 'Process',
    tooltip: 'Operating temperature in degrees',
  },
  {
    id: 'param-037',
    name: 'PiX_PC_3_DesignPressure',
    category: 'Process',
    dataType: 'number',
    description: 'Design pressure',
    isInstance: true,
    group: 'Process',
    tooltip: 'Design pressure in bar/psi',
  },
  {
    id: 'param-038',
    name: 'PiX_PC_4_OperatingPressure',
    category: 'Process',
    dataType: 'number',
    description: 'Operating pressure',
    isInstance: true,
    group: 'Process',
    tooltip: 'Operating pressure in bar/psi',
  },
  {
    id: 'param-039',
    name: 'PiX_PC_5_FlowRate',
    category: 'Process',
    dataType: 'number',
    description: 'Flow rate',
    isInstance: true,
    group: 'Process',
    tooltip: 'Flow rate in m3/h or gpm',
  },
  {
    id: 'param-040',
    name: 'PiX_PC_6_Density',
    category: 'Process',
    dataType: 'number',
    description: 'Fluid density',
    isInstance: true,
    group: 'Process',
    tooltip: 'Fluid density in kg/m3',
  },
  {
    id: 'param-041',
    name: 'PiX_PC_7_Viscosity',
    category: 'Process',
    dataType: 'number',
    description: 'Fluid viscosity',
    isInstance: true,
    group: 'Process',
    tooltip: 'Fluid viscosity in cP',
  },

  // -------------------------------------------------------------------------
  // Geometry Parameters
  // -------------------------------------------------------------------------
  {
    id: 'param-042',
    name: 'PiX_GE_1_NominalDiameter',
    category: 'Geometry',
    dataType: 'length',
    description: 'Nominal diameter',
    isInstance: false,
    group: 'Geometry',
    tooltip: 'Nominal pipe diameter',
  },
  {
    id: 'param-043',
    name: 'PiX_GE_2_OutsideDiameter',
    category: 'Geometry',
    dataType: 'length',
    description: 'Outside diameter',
    isInstance: false,
    group: 'Geometry',
    tooltip: 'Pipe outside diameter',
  },
  {
    id: 'param-044',
    name: 'PiX_GE_3_WallThickness',
    category: 'Geometry',
    dataType: 'length',
    description: 'Wall thickness',
    isInstance: false,
    group: 'Geometry',
    tooltip: 'Pipe wall thickness',
  },
  {
    id: 'param-045',
    name: 'PiX_GE_4_Schedule',
    category: 'Geometry',
    dataType: 'string',
    description: 'Pipe schedule',
    isInstance: false,
    group: 'Geometry',
    tooltip: 'Pipe schedule (SCH40, SCH80, etc.)',
  },
  {
    id: 'param-046',
    name: 'PiX_GE_5_Length',
    category: 'Geometry',
    dataType: 'length',
    description: 'Component length',
    isInstance: true,
    group: 'Geometry',
    tooltip: 'Total length of the component',
  },
  {
    id: 'param-047',
    name: 'PiX_GE_6_Angle',
    category: 'Geometry',
    dataType: 'angle',
    description: 'Fitting angle',
    isInstance: false,
    group: 'Geometry',
    tooltip: 'Angle for fittings (45, 90 degrees)',
  },
];

// =============================================================================
// Mock Parameter Options (for dropdowns)
// =============================================================================

/**
 * Parameter options grouped by category for use in dropdown selections.
 * These match the REVIT_PARAMETER_OPTIONS used in MappingTableSection.
 */
export const MOCK_PARAMETER_OPTIONS: RevitParameterOption[] = [
  // Piping
  { value: 'PiP_PPG_SPEC', label: 'PiP_PPG_SPEC', group: 'Piping' },

  // Insulation
  { value: 'Insulation Type', label: 'Insulation Type', group: 'Insulation' },
  { value: 'Insulation Thickness', label: 'Insulation Thickness', group: 'Insulation' },
  { value: 'INSULATIONTHICKNESS', label: 'INSULATIONTHICKNESS', group: 'Insulation' },

  // Tracing
  { value: 'PiX_GN_2_HeatTracingSpec', label: 'PiX_GN_2_HeatTracingSpec', group: 'Tracing' },

  // Weight
  { value: 'PiX_06_Mass_kg_Piping', label: 'PiX_06_Mass_kg_Piping', group: 'Weight' },

  // Pressure
  { value: 'PiX_PPG_END1_PressureRating', label: 'PiX_PPG_END1_PressureRating', group: 'Pressure' },
  { value: 'PressureClass', label: 'PressureClass', group: 'Pressure' },

  // Material
  { value: 'PiX_03_Material_ENG', label: 'PiX_03_Material_ENG', group: 'Material' },
  { value: 'Material', label: 'Material', group: 'Material' },

  // Standard
  { value: 'PiX_02_Standard_ENG', label: 'PiX_02_Standard_ENG', group: 'Standard' },
  { value: 'DesignStd', label: 'DesignStd', group: 'Standard' },

  // Endpoints
  { value: 'END-POINT_1', label: 'END-POINT_1', group: 'Endpoints' },
  { value: 'END-POINT_2', label: 'END-POINT_2', group: 'Endpoints' },
  { value: 'END-POINT_3', label: 'END-POINT_3', group: 'Endpoints' },
  { value: 'END-POINT_4', label: 'END-POINT_4', group: 'Endpoints' },

  // General
  { value: 'Description', label: 'Description', group: 'General' },
  { value: 'SKEY', label: 'SKEY', group: 'General' },
  { value: 'OBJECT-TYPE', label: 'OBJECT-TYPE', group: 'General' },
  { value: 'ITEM-DESCRIPTION', label: 'ITEM-DESCRIPTION', group: 'General' },
];

// =============================================================================
// Mock Revit Elements
// =============================================================================

/**
 * Mock Revit elements representing piping components
 */
export const MOCK_REVIT_ELEMENTS: RevitElement[] = [
  {
    id: 'elem-001',
    name: 'Pipe - Carbon Steel',
    type: 'Family',
    category: 'Pipes',
    familyName: 'Pipe Types',
    typeName: 'Standard',
    parameters: MOCK_REVIT_PARAMETERS.filter((p) =>
      ['Specifications', 'Material', 'Geometry', 'Insulation'].includes(p.category)
    ),
    isSelected: false,
  },
  {
    id: 'elem-002',
    name: 'Elbow - 90 LR',
    type: 'Family',
    category: 'PipeFittings',
    familyName: 'Elbow - Generic',
    typeName: '90 Degree Long Radius',
    parameters: MOCK_REVIT_PARAMETERS.filter((p) =>
      ['Connections', 'Material', 'Geometry', 'Pressure'].includes(p.category)
    ),
    parameterValues: [
      { parameterId: 'param-001', parameterName: 'PiX_PPG_END1_TYPE', value: 'FL', isReadOnly: false, isInstance: true },
      { parameterId: 'param-002', parameterName: 'PiX_PPG_END2_TYPE', value: 'BV', isReadOnly: false, isInstance: true },
      { parameterId: 'param-003', parameterName: 'PiX_PPG_END3_TYPE', value: '', isReadOnly: false, isInstance: true },
    ],
    isSelected: false,
  },
  {
    id: 'elem-003',
    name: 'Elbow - 45',
    type: 'Family',
    category: 'PipeFittings',
    familyName: 'Elbow - Generic',
    typeName: '45 Degree',
    parameters: MOCK_REVIT_PARAMETERS.filter((p) =>
      ['Connections', 'Material', 'Geometry', 'Pressure'].includes(p.category)
    ),
    parameterValues: [
      { parameterId: 'param-001', parameterName: 'PiX_PPG_END1_TYPE', value: 'FL', isReadOnly: false, isInstance: true },
      { parameterId: 'param-002', parameterName: 'PiX_PPG_END2_TYPE', value: 'BV', isReadOnly: false, isInstance: true },
      { parameterId: 'param-003', parameterName: 'PiX_PPG_END3_TYPE', value: '', isReadOnly: false, isInstance: true },
    ],
    isSelected: false,
  },
  {
    id: 'elem-004',
    name: 'Tee - Equal',
    type: 'Family',
    category: 'PipeFittings',
    familyName: 'Tee - Generic',
    typeName: 'Equal',
    parameters: MOCK_REVIT_PARAMETERS.filter((p) =>
      ['Connections', 'Material', 'Geometry', 'Pressure'].includes(p.category)
    ),
    isSelected: false,
  },
  {
    id: 'elem-005',
    name: 'Reducer - Concentric',
    type: 'Family',
    category: 'PipeFittings',
    familyName: 'Reducer - Generic',
    typeName: 'Concentric',
    parameters: MOCK_REVIT_PARAMETERS.filter((p) =>
      ['Connections', 'Material', 'Geometry'].includes(p.category)
    ),
    isSelected: false,
  },
  {
    id: 'elem-006',
    name: 'Reducer - Eccentric',
    type: 'Family',
    category: 'PipeFittings',
    familyName: 'Reducer - Generic',
    typeName: 'Eccentric',
    parameters: MOCK_REVIT_PARAMETERS.filter((p) =>
      ['Connections', 'Material', 'Geometry'].includes(p.category)
    ),
    isSelected: false,
  },
  {
    id: 'elem-007',
    name: 'Flange - Weld Neck',
    type: 'Family',
    category: 'PipeFittings',
    familyName: 'Flange - Generic',
    typeName: 'Weld Neck',
    parameters: MOCK_REVIT_PARAMETERS.filter((p) =>
      ['Connections', 'Material', 'Pressure'].includes(p.category)
    ),
    isSelected: false,
  },
  {
    id: 'elem-008',
    name: 'Flange - Slip On',
    type: 'Family',
    category: 'PipeFittings',
    familyName: 'Flange - Generic',
    typeName: 'Slip On',
    parameters: MOCK_REVIT_PARAMETERS.filter((p) =>
      ['Connections', 'Material', 'Pressure'].includes(p.category)
    ),
    isSelected: false,
  },
  {
    id: 'elem-009',
    name: 'Gate Valve',
    type: 'Family',
    category: 'PipeAccessories',
    familyName: 'Valve - Gate',
    typeName: 'Standard',
    parameters: MOCK_REVIT_PARAMETERS.filter((p) =>
      ['Connections', 'Material', 'Pressure', 'Identity'].includes(p.category)
    ),
    isSelected: false,
  },
  {
    id: 'elem-010',
    name: 'Ball Valve',
    type: 'Family',
    category: 'PipeAccessories',
    familyName: 'Valve - Ball',
    typeName: 'Standard',
    parameters: MOCK_REVIT_PARAMETERS.filter((p) =>
      ['Connections', 'Material', 'Pressure', 'Identity'].includes(p.category)
    ),
    isSelected: false,
  },
];

// =============================================================================
// Mock Revit Families
// =============================================================================

/**
 * Mock Revit families
 */
export const MOCK_REVIT_FAMILIES: RevitFamily[] = [
  {
    id: 'fam-001',
    name: 'Pipe Types',
    category: 'Pipes',
    types: [
      { id: 'type-001', name: 'Standard' },
      { id: 'type-002', name: 'Heavy Wall' },
      { id: 'type-003', name: 'Extra Heavy' },
    ],
    parameters: MOCK_REVIT_PARAMETERS.filter((p) => p.category === 'Specifications'),
  },
  {
    id: 'fam-002',
    name: 'Elbow - Generic',
    category: 'PipeFittings',
    types: [
      { id: 'type-004', name: '90 Degree Long Radius' },
      { id: 'type-005', name: '90 Degree Short Radius' },
      { id: 'type-006', name: '45 Degree' },
    ],
    parameters: MOCK_REVIT_PARAMETERS.filter((p) => p.category === 'Connections'),
  },
  {
    id: 'fam-003',
    name: 'Flange - Generic',
    category: 'PipeFittings',
    types: [
      { id: 'type-007', name: 'Weld Neck' },
      { id: 'type-008', name: 'Slip On' },
      { id: 'type-009', name: 'Blind' },
    ],
    parameters: MOCK_REVIT_PARAMETERS.filter((p) => p.category === 'Pressure'),
  },
];

// =============================================================================
// Mock Revit Settings
// =============================================================================

/**
 * Mock Revit project settings
 */
export const MOCK_REVIT_SETTINGS: RevitSettings = {
  lastUpdated: new Date().toISOString(),
  revitProjectName: 'Sample Piping Project',
  revitVersion: '2025',
  activeView: '3D View - Mechanical',
  units: 'Metric',
  projectPath: 'C:\\Projects\\SampleProject\\PipingModel.rvt',
  hasUnsavedChanges: false,
  userName: 'Designer',
  projectInfo: {
    projectNumber: 'PRJ-2025-001',
    projectName: 'Industrial Plant Piping',
    clientName: 'Acme Industries',
    buildingName: 'Process Building A',
    address: '123 Industrial Way',
    status: 'In Progress',
  },
};

// =============================================================================
// Mock Connector Data
// =============================================================================

/**
 * Mock connector information for a typical tee fitting
 */
export const MOCK_CONNECTOR_INFO: RevitConnectorInfo[] = [
  {
    id: 'conn-001',
    portNumber: 1,
    isPrimary: true,
    isSecondary: false,
    connectionType: 'BW',
    direction: { x: 1, y: 0, z: 0 },
    origin: { x: 0, y: 0, z: 0 },
    size: 100,
  },
  {
    id: 'conn-002',
    portNumber: 2,
    isPrimary: false,
    isSecondary: true,
    connectionType: 'BW',
    direction: { x: -1, y: 0, z: 0 },
    origin: { x: 200, y: 0, z: 0 },
    size: 100,
  },
  {
    id: 'conn-003',
    portNumber: 3,
    isPrimary: false,
    isSecondary: false,
    connectionType: 'BW',
    direction: { x: 0, y: 1, z: 0 },
    origin: { x: 100, y: 100, z: 0 },
    size: 80,
  },
];

/**
 * Mock end type information
 */
export const MOCK_END_TYPES: RevitEndType[] = [
  { endNumber: 1, type: 'BW', pressureRating: 'CLASS 150', size: 100 },
  { endNumber: 2, type: 'BW', pressureRating: 'CLASS 150', size: 100 },
  { endNumber: 3, type: 'FL', pressureRating: 'CLASS 300', size: 80 },
];

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get parameters by category
 */
export function getParametersByCategory(category: RevitParameterGroup): RevitParameter[] {
  return MOCK_REVIT_PARAMETERS.filter((p) => p.category === category);
}

/**
 * Get parameters by group (for dropdown grouping)
 */
export function getParametersByGroup(group: string): RevitParameter[] {
  return MOCK_REVIT_PARAMETERS.filter((p) => p.group === group);
}

/**
 * Find parameter by name
 */
export function findParameterByName(name: string): RevitParameter | undefined {
  return MOCK_REVIT_PARAMETERS.find((p) => p.name === name);
}

/**
 * Get unique parameter groups
 */
export function getUniqueGroups(): string[] {
  return Array.from(new Set(MOCK_REVIT_PARAMETERS.map((p) => p.group).filter(Boolean))) as string[];
}

/**
 * Get unique parameter categories
 */
export function getUniqueCategories(): RevitParameterGroup[] {
  return Array.from(new Set(MOCK_REVIT_PARAMETERS.map((p) => p.category)));
}
